import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { type CountryCode } from 'libphonenumber-js';
import {
  exceedsNationalLength,
  formatPhoneNational,
  getPhoneCountryOptions,
  normalizeNationalDigits,
  splitInternationalNumber,
  validateNationalPhone,
  type PhoneCountryOption,
} from '../../utils/international-phone';

@Component({
  selector: 'app-intl-phone-field',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './intl-phone-field.component.html',
  styleUrl: './intl-phone-field.component.css',
})
export class IntlPhoneFieldComponent implements OnChanges {
  @Input({ required: true }) label = '';
  @Input({ required: true }) inputId = '';
  /** Angular template name for the national input (forms). */
  @Input({ required: true }) name = '';
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() required = false;

  @Input() countryIso: CountryCode = 'IN';
  @Output() countryIsoChange = new EventEmitter<CountryCode>();

  @Input() nationalNumber = '';
  @Output() nationalNumberChange = new EventEmitter<string>();

  readonly countryOptions: PhoneCountryOption[] = getPhoneCountryOptions();

  fieldError = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['nationalNumber'] && !changes['countryIso']) {
      return;
    }
    const raw = this.nationalNumber ?? '';
    if (!raw.trim()) {
      return;
    }
    const formatted = formatPhoneNational(this.countryIso, raw);
    if (formatted !== raw) {
      this.nationalNumber = formatted;
      queueMicrotask(() => this.nationalNumberChange.emit(formatted));
    }
  }

  onCountryIsoChange(value: string): void {
    const next = value as CountryCode;
    this.countryIso = next;
    this.countryIsoChange.emit(next);
    if (this.nationalNumber.trim()) {
      const reformatted = formatPhoneNational(next, this.nationalNumber);
      if (reformatted !== this.nationalNumber) {
        this.nationalNumber = reformatted;
        this.nationalNumberChange.emit(reformatted);
      }
    }
    this.refreshError();
  }

  /**
   * Cleans what arrives, then writes the result back to the element.
   *
   * Binding alone is not enough. Pasting text with no digits in it cleans to the value the model
   * already held, so Angular sees no change, renders nothing, and the pasted words stay on screen
   * looking accepted. Setting the element's own value is what removes them.
   */
  onNationalChange(value: string, el?: HTMLInputElement): void {
    const international = splitInternationalNumber(value);
    if (international) {
      this.countryIso = international.iso;
      this.countryIsoChange.emit(international.iso);
      const national = formatPhoneNational(international.iso, international.national);
      this.nationalNumber = national;
      this.nationalNumberChange.emit(national);
      this.fieldError = '';
      if (el) el.value = national;
      return;
    }

    const formatted = formatPhoneNational(this.countryIso, value);
    this.nationalNumber = formatted;
    this.nationalNumberChange.emit(formatted);
    this.fieldError = '';
    if (el) el.value = formatted;
  }

  onNationalBlur(): void {
    this.refreshError();
  }

  onNationalKeydown(ev: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }
    const nav = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
    ];
    if (nav.includes(ev.key) || ev.ctrlKey || ev.metaKey || ev.altKey) {
      return;
    }
    if (ev.key.length === 1 && !/\d/.test(ev.key)) {
      ev.preventDefault();
      return;
    }
    if (ev.key.length === 1 && /\d/.test(ev.key)) {
      const next = normalizeNationalDigits(this.nationalNumber) + ev.key;
      // Every country, not just India: the library knows the possible lengths, including the
      // ones that vary, so a number cannot be typed past what its country allows.
      if (exceedsNationalLength(this.countryIso, next)) {
        ev.preventDefault();
      }
    }
  }

  /** Call from parent after submit validation to show errors inline. */
  refreshError(): void {
    this.fieldError = validateNationalPhone(this.countryIso, this.nationalNumber) ?? '';
  }

  clearError(): void {
    this.fieldError = '';
  }
}
